/* eslint-disable react/require-default-props */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from 'react';
import {
  fetchBulletinBiddings,
  fetchBulletinFollowups,
  clearIaToolAction,
  clearIaMessages,
  setIaMessages,
  setIaAppliedFilter,
  clearIaAppliedFilter,
} from 'store/modules/bulletins_store/actions';
import { esferasOptions } from 'services/esferas_contracts';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
  BulletinsState,
  BulletinsTypes,
  IaChatMessageTypes,
} from 'store/modules/bulletins_store/types';
import Select from 'react-select';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { DateRangePicker, FocusedInputShape } from 'react-dates';
import moment from 'moment';
import iconIA from 'assets/images/icone-ia.svg';
import {
  createCustomSearch,
  updateCustomSearch,
  deleteCustomSearch,
} from 'services/actions/custom_search';
import ModalRemoveSearch from 'components/atoms/modal_remove_search';
import { ProfileState } from 'store/modules/profile_store/types';
import {
  CitiesTypes,
  OnlineSearchState,
  OnlineSearchTypes,
} from 'store/modules/online_search_store/types';
import { States } from 'services/states';
import { Regions, RegionsStates } from 'services/regions';
import { getCitiesByStates } from 'services/actions/cities_actions';
import BulletinIaChat from 'components/organisms/bulletin_ia_chat';
import ModalLocation from 'components/atoms/modal_location';
import companyId from 'services/company_id';
import createTrack from 'services/actions/tracker_actions';
import { Container } from './styles';
import { initialFilters } from './initialFilters';

const defaultStatusOptions = [
  { value: false, label: 'Não lida' },
  { value: true, label: 'Lida' },
];
type BulletinSearchProps = {
  bulletinId?: string;
  selected: 'licitacoes' | 'acompanhamentos';
  onChangeSelected: (tab: 'licitacoes' | 'acompanhamentos') => void;
  registerEditFilter?: (
    fn: (name: string, ai: boolean, chatHistory?: IaChatMessageTypes[], searchId?: number) => void,
  ) => void;
  registerDeleteFilter?: (fn: () => void) => void;
  registerSaveAppliedFilter?: (fn: (chatHistory?: IaChatMessageTypes[]) => void) => void;
  onSearchesChanged?: () => void;
  matchMode?: boolean;
};

const BulletinSearch: React.FC<BulletinSearchProps> = ({
  bulletinId = '1',
  selected,
  onChangeSelected,
  registerEditFilter,
  registerDeleteFilter,
  registerSaveAppliedFilter,
  onSearchesChanged,
  matchMode = false,
}) => {
  const dispatch = useDispatch();
  const ordemMatch = matchMode ? { ordem: 'match' as const } : {};
  const { bulletins } = useSelector<ApplicationState, BulletinsState>(
    (state) => state.session.bulletins,
  );
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const { onlineSearch } = useSelector<ApplicationState, OnlineSearchState>(
    (state) => state.session.onlineSearch,
  );
  const [filters, setFilters] = useState(initialFilters);
  const [citiesList, setCitiesList] = useState<Array<CitiesTypes>>([]);
  const [situationOptions, setSituationOptions] = useState<{ value: number; label: string }[]>([]);
  const [statusOptions, setStatusOptions] = useState(defaultStatusOptions);
  const [focusedValidityDate, setFocusedValidityDate] = useState<FocusedInputShape | null>(null);
  const [focusedPublicationDate, setFocusedPublicationDate] = useState<FocusedInputShape | null>(
    null,
  );
  const [matchOnly, setMatchOnly] = useState(false);
  const [saveFilter, setSaveFilter] = useState(false);
  const [updateFilter, setUpdateFilter] = useState(false);
  const [filterName, setFilterName] = useState('');
  const skipFilterActiveEffect = useRef(false);
  const isInitialMount = useRef(true);
  const [modalRemoveSearchOpened, setModalRemoveSearchOpened] = useState(false);
  const [showIaChat, setShowIaChat] = useState(false);
  const [autoSendSave, setAutoSendSave] = useState(false);
  const [editingCustomSearchId, setEditingCustomSearchId] = useState<number | null>(null);
  const [editingCustomSearchName, setEditingCustomSearchName] = useState<string>('');
  const [modalLocationOpened, setModalLocationOpened] = useState(false);
  const [addressSelected, setAddressSelected] = useState('');
  const [bounds, setBounds] = useState<{
    ne: { lat: Number | undefined; lng: Number | undefined };
    sw: { lat: Number | undefined; lng: Number | undefined };
  }>({
    ne: { lat: 0, lng: 0 },
    sw: { lat: 0, lng: 0 },
  });
  const [position, setPosition] = useState<google.maps.LatLngLiteral | null>(null);
  const [citiesGeolocation, setCitiesGeolocation] = useState<Array<CitiesTypes>>([]);
  const iaToolAction = bulletins?.ia_chat_tool_action;
  const iaMessages = bulletins?.ia_chat_messages || [];
  const biddingsCount =
    bulletins?.bulletins_biddings_list?.[bulletinId]?.total_entries ?? undefined;
  const followupsCount =
    bulletins?.bulletins_followups_list?.[bulletinId]?.total_entries ?? undefined;

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setCitiesList([]);
    setMatchOnly(false);
    setSaveFilter(false);
    dispatch(clearIaAppliedFilter());
    setAddressSelected('');
    setPosition(null);
    setBounds({ ne: { lat: 0, lng: 0 }, sw: { lat: 0, lng: 0 } });
    setCitiesGeolocation([]);

    if (selected === 'acompanhamentos') {
      dispatch(
        fetchBulletinFollowups({
          bulletin_id: bulletinId,
          filters: initialFilters,
          isSearch: false,
        }),
      );
    } else {
      dispatch(
        fetchBulletinBiddings({
          ...ordemMatch,
          bulletin_id: bulletinId,
          companyId: profile.representant ? companyId(profile) : undefined,
          filters: initialFilters,
          isSearch: false,
        }),
      );
    }
  };

  const handleFilterGeolocationType = (type: 'estado' | 'região' | 'localização') => {
    setFilters((prev) => ({
      ...prev,
      geoType: type,
      region: null,
      states: [],
      cities: [],
      address: '',
    }));
    setCitiesList([]);
    setCitiesGeolocation([]);
  };

  const handleRadiusChange: React.Dispatch<React.SetStateAction<number>> = (value) => {
    const resolved = typeof value === 'function' ? value(filters.radius) : value;
    setFilters((prev) => ({ ...prev, radius: resolved }));
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };
  const handleSearch = async () => {
    dispatch(clearIaAppliedFilter());
    if (selected === 'acompanhamentos') {
      dispatch(
        fetchBulletinFollowups({
          bulletin_id: bulletinId,
          filters,
          isSearch: true,
        }),
      );
    } else {
      dispatch(
        fetchBulletinBiddings({
          ...ordemMatch,
          bulletin_id: bulletinId,
          companyId: profile.representant ? companyId(profile) : undefined,
          filters,
          isSearch: true,
        }),
      );
    }

    if (saveFilter && filterName.trim()) {
      const result = await createCustomSearch({
        contact_id: profile.id,
        name: filterName,
        filters: filters as any,
        service_id: 1,
        ai: false,
      });
      if (result?.success && result?.filter) {
        dispatch({
          type: OnlineSearchTypes.FILTER_ACTIVE,
          filterActive: { id: result.filter.id, name: result.filter.name },
        });
        setSaveFilter(false);
        setFilterName('');
        onSearchesChanged?.();
      }
    }
  };

  const handleUpdateFilter = async () => {
    const currentFilters = { ...filters };
    const updatedName = filterName;
    const result = await updateCustomSearch(onlineSearch.filterActive.id, {
      contact_id: profile.id,
      name: updatedName,
      filters: currentFilters as any,
      service_id: 1,
    });
    if (result?.success) {
      setUpdateFilter(false);
      setFilterName('');

      if (selected === 'acompanhamentos') {
        dispatch(
          fetchBulletinFollowups({
            bulletin_id: bulletinId,
            filters: currentFilters,
            isSearch: true,
          }),
        );
      } else {
        dispatch(
          fetchBulletinBiddings({
            ...ordemMatch,
            bulletin_id: bulletinId,
            companyId: profile.representant ? companyId(profile) : undefined,
            filters: currentFilters,
            isSearch: true,
          }),
        );
      }

      skipFilterActiveEffect.current = true;
      dispatch({
        type: OnlineSearchTypes.FILTER_ACTIVE,
        filterActive: {
          id: result.filter?.id ?? onlineSearch.filterActive.id,
          name: result.filter?.name ?? updatedName,
        },
      });
      onSearchesChanged?.();
    }
  };

  const handleDeleteFilter = async () => {
    const result = await deleteCustomSearch(onlineSearch.filterActive.id);
    if (result?.success) {
      dispatch({ type: OnlineSearchTypes.CLEAR_FILTER });
      setModalRemoveSearchOpened(false);
      handleClearFilters();
      onSearchesChanged?.();
    }
  };

  const handleNewSearch = () => {
    dispatch({ type: OnlineSearchTypes.CLEAR_FILTER });
    setUpdateFilter(false);
    setSaveFilter(false);
    setFilterName('');
    handleClearFilters();
  };

  useEffect(() => {
    dispatch({ type: OnlineSearchTypes.CLEAR_FILTER });
    dispatch(clearIaAppliedFilter());
  }, []);

  useEffect(() => {
    if (!onlineSearch.filterActive?.id) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (skipFilterActiveEffect.current) {
      skipFilterActiveEffect.current = false;
      return;
    }

    setUpdateFilter(false);
    setFilterName('');

    const savedFilters = onlineSearch.filters as any;
    const newFilters = {
      ...initialFilters,
      objeto: savedFilters.objeto || '',
      notWord: savedFilters.notWord || '',
      geoType: savedFilters.geoType || 'estado',
      region: savedFilters.region || null,
      states: savedFilters.states || [],
      cities: savedFilters.cities || [],
      address: savedFilters.address || '',
      radius: savedFilters.radius || 10000,
      validityStart: savedFilters.validityStart || '',
      validityEnd: savedFilters.validityEnd || '',
      situations: savedFilters.situations || [],
      status: savedFilters.status || [],
      orgao: savedFilters.orgao || '',
      esfera: savedFilters.esfera || [],
      publicationStart: savedFilters.publicationStart || '',
      publicationEnd: savedFilters.publicationEnd || '',
    };

    setFilters(newFilters);

    if (selected === 'acompanhamentos') {
      dispatch(
        fetchBulletinFollowups({
          bulletin_id: bulletinId,
          filters: newFilters,
          isSearch: true,
        }),
      );
    } else {
      dispatch(
        fetchBulletinBiddings({
          ...ordemMatch,
          bulletin_id: bulletinId,
          companyId: profile.representant ? companyId(profile) : undefined,
          filters: newFilters,
          isSearch: true,
        }),
      );
    }
  }, [onlineSearch.filterActive]);

  const handleApplyIaFilters = () => {
    if (!iaToolAction?.filters) return;

    const currentFilters = {
      ...initialFilters,
      objeto: iaToolAction.filters.objeto || '',
      notWord: iaToolAction.filters.notWord || '',
      states: iaToolAction.filters.states || [],
      cities: iaToolAction.filters.cities || [],
      validityStart: iaToolAction.filters.validityStart || '',
      validityEnd: iaToolAction.filters.validityEnd || '',
      situations: iaToolAction.filters.situations || [],
      status: iaToolAction.filters.status || [],
      orgao: iaToolAction.filters.orgao || '',
      esfera: iaToolAction.filters.esfera || [],
      publicationStart: iaToolAction.filters.publicationStart || '',
      publicationEnd: iaToolAction.filters.publicationEnd || '',
    };

    setFilters(currentFilters);
    setShowIaChat(false);

    if (selected === 'acompanhamentos') {
      dispatch(
        fetchBulletinFollowups({
          bulletin_id: bulletinId,
          filters: currentFilters,
          isSearch: true,
        }),
      );
    } else {
      dispatch(
        fetchBulletinBiddings({
          ...ordemMatch,
          bulletin_id: bulletinId,
          companyId: profile.representant ? companyId(profile) : undefined,
          filters: currentFilters,
          isSearch: true,
        }),
      );
    }

    const appliedName = iaToolAction.name || iaToolAction.filters.objeto || 'Filtro IA';

    if (editingCustomSearchId && editingCustomSearchId > 0) {
      skipFilterActiveEffect.current = true;
      dispatch({
        type: OnlineSearchTypes.FILTER_ACTIVE,
        filterActive: { id: editingCustomSearchId, name: editingCustomSearchName || appliedName },
      });
      dispatch(clearIaAppliedFilter());
    } else {
      dispatch(setIaAppliedFilter({ name: appliedName, chatHistory: iaMessages }));
    }
    dispatch(clearIaToolAction());
  };

  const handleSaveIaFilter = async (
    filterData: any,
    name: string,
    chatHistory: IaChatMessageTypes[],
  ) => {
    const formFilters = {
      ...initialFilters,
      objeto: filterData.objeto || '',
      notWord: filterData.notWord || '',
      states: filterData.states || [],
      cities: filterData.cities || [],
      validityStart: filterData.validityStart || '',
      validityEnd: filterData.validityEnd || '',
      situations: filterData.situations || [],
      status: filterData.status || [],
      orgao: filterData.orgao || '',
      esfera: filterData.esfera || [],
      publicationStart: filterData.publicationStart || '',
      publicationEnd: filterData.publicationEnd || '',
    };

    const filtersToSave = {
      ...formFilters,
      ia_chat_history: chatHistory,
    };

    const filterName = name || formFilters.objeto || 'Filtro IA';
    const targetId = editingCustomSearchId || onlineSearch.filterActive.id;

    const isApplied = !!bulletins?.ia_applied_filter || onlineSearch.filterActive.id > 0;

    let savedId: number | undefined;
    let savedName = filterName;

    if (targetId && targetId > 0) {
      const result = await updateCustomSearch(targetId, {
        contact_id: profile.id,
        name: filterName,
        filters: filtersToSave as any,
        service_id: 1,
        ai: true,
      });
      if (result?.success) {
        savedId = result.filter?.id ?? targetId;
        savedName = result.filter?.name ?? filterName;
      }
    } else {
      const result = await createCustomSearch({
        contact_id: profile.id,
        name: filterName,
        filters: filtersToSave as any,
        service_id: 1,
        ai: true,
      });
      if (result?.success && result?.filter) {
        savedId = result.filter.id;
        savedName = result.filter.name;
      }
    }

    if (!savedId) return;

    setEditingCustomSearchId(savedId);
    setEditingCustomSearchName(savedName);

    if (isApplied) {
      skipFilterActiveEffect.current = true;
      dispatch({
        type: OnlineSearchTypes.FILTER_ACTIVE,
        filterActive: { id: savedId, name: savedName },
      });
    }
    dispatch(clearIaAppliedFilter());
    onSearchesChanged?.();
  };

  useEffect(() => {
    (async () => {
      if (filters.states.length > 0) {
        const citiesResponse = await getCitiesByStates(filters.states);

        setCitiesList(
          citiesResponse?.map((city: any) => ({
            label: `${city.name_sub} - ${city.acronym}`,
            value: city.id,
          })),
        );
      } else {
        setCitiesList([]);
      }
    })();
  }, [filters.states]);

  useEffect(() => {
    if (citiesGeolocation?.length === 0 || citiesGeolocation === undefined) return;

    setFilters((prev) => ({
      ...prev,
      cities: citiesGeolocation.map((c) => Number(c.value)),
      address: addressSelected,
    }));
  }, [citiesGeolocation]);

  useEffect(() => {
    dispatch({
      type: BulletinsTypes.SET_STATUS_FILTER,
      status_filter: filters.status,
    });
  }, [filters.status]);

  // Modo Match: o recorte "somente compatíveis" é client-side, então aplica na hora
  // (sem passar pelo botão Filtrar, que dispara nova busca no backend).
  useEffect(() => {
    dispatch({
      type: BulletinsTypes.SET_MATCH_ONLY_FILTER,
      match_only_filter: matchMode && matchOnly,
    });
  }, [matchMode, matchOnly]);

  useEffect(() => {
    const lists = bulletins?.bulletins_biddings_list || {};
    const allBiddings = Object.values(lists)
      .map((item: any) => item?.biddings || [])
      .flat();

    const map = new Map<number, string>();
    const statusSet = new Set<boolean>();
    allBiddings.forEach((bidding: any) => {
      const id = bidding?.bidding_grouping?.id;
      const label = bidding?.bidding_grouping?.resumo || bidding?.bidding_grouping?.nome;
      if (id && label && !map.has(id)) map.set(id, label);

      if (typeof bidding?.viewed_by_user === 'boolean') {
        statusSet.add(bidding.viewed_by_user);
      }
    });

    if (map.size > 0) setSituationOptions(Array.from(map, ([value, label]) => ({ value, label })));

    if (statusSet.size > 0) {
      const options = Array.from(statusSet).map((value) => ({
        value,
        label: value ? 'Lida' : 'Não lida',
      }));
      options.sort((a, b) => Number(a.value) - Number(b.value));
      setStatusOptions(options);
    } else {
      setStatusOptions(defaultStatusOptions);
    }
  }, [bulletins?.bulletins_biddings_list]);

  useEffect(() => {
    registerEditFilter?.(
      (name: string, ai: boolean, chatHistory?: IaChatMessageTypes[], searchId?: number) => {
        if (ai) {
          if (chatHistory && chatHistory.length > 0) {
            dispatch(setIaMessages(chatHistory));
          }
          setEditingCustomSearchId(searchId || null);
          setEditingCustomSearchName(name || '');
          setShowIaChat(true);
        } else {
          setUpdateFilter(true);
          setFilterName(name);
        }
      },
    );
    registerDeleteFilter?.(() => {
      setModalRemoveSearchOpened(true);
    });
    registerSaveAppliedFilter?.((chatHistory?: IaChatMessageTypes[]) => {
      if (chatHistory && chatHistory.length > 0) {
        dispatch(setIaMessages(chatHistory));
      }
      setEditingCustomSearchId(null);
      setAutoSendSave(true);
      setShowIaChat(true);
    });
  }, [registerEditFilter, registerDeleteFilter, registerSaveAppliedFilter]);

  return (
    <Container className="col-md-3 bg-white px-3 position-fixed">
      <div className="p-2 bg-light rounded mt-3">
        <div className="btn-group w-100 toggle-wrapper">
          <button
            type="button"
            className={`btn toggle-btn rounded ${selected === 'licitacoes' ? 'active' : ''}`}
            onClick={() => onChangeSelected('licitacoes')}
          >
            Licitações
            <span className="badge badge-secondary ml-2">
              {typeof biddingsCount === 'number' ? biddingsCount : '-'}
            </span>
          </button>

          <button
            type="button"
            className={`btn toggle-btn rounded ${selected === 'acompanhamentos' ? 'active' : ''}`}
            onClick={() => onChangeSelected('acompanhamentos')}
          >
            Acompanhamentos
            <span className="badge badge-dark ml-2">
              {typeof followupsCount === 'number' ? followupsCount : '-'}
            </span>
          </button>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <button
            type="button"
            className="btn btn-secondary btn-lg btn-block btn-ia d-flex align-items-center justify-content-center mt-3"
            onClick={() => {
              dispatch(clearIaMessages());
              setEditingCustomSearchId(null);
              setAutoSendSave(false);
              setShowIaChat(true);
              createTrack('Boletim - Clicou botão de IA');
            }}
            style={{ gap: 8 }}
            disabled={updateFilter}
          >
            <img
              alt="Ícone IA"
              src={iconIA}
              height="20"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
            <span>Filtrar com IA</span>
          </button>
        </div>
      </div>
      <hr />
      <form
        className={onlineSearch.filterActive.id > 0 && !updateFilter ? 'filter-active' : ''}
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch();
        }}
      >
        <div className="form-row">
          <div className="form-group col-md-12">
            <label htmlFor="inputEmail4">Palavra-chave</label>
            <input
              type="text"
              name="objeto"
              className="form-control"
              placeholder="Buscar no objeto e itens"
              value={filters.objeto}
              onChange={handleFilterChange}
            />
          </div>
          <div className="form-group col-md-12">
            <label htmlFor="inputEmail4">Não conter</label>
            <input
              type="text"
              name="notWord"
              className="form-control"
              placeholder="Digite o item que não deve conter no resultado"
              value={filters.notWord}
              onChange={handleFilterChange}
            />
          </div>
          {matchMode && (
            <div className="form-group col-md-12 mb-0">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="matchOnlyFilter"
                  checked={matchOnly}
                  onChange={() => setMatchOnly((prev) => !prev)}
                />
                <label className="form-check-label" htmlFor="matchOnlyFilter">
                  Somente licitações compatíveis
                </label>
              </div>
            </div>
          )}
          <hr />
        </div>
        <div className="row mb-3">
          <div className="col-12">
            <p className="mb-1">Filtrar por:</p>
            <div className="d-flex justify-content-between">
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="bulletinGeoType"
                  id="bulletinGeoEstado"
                  onChange={() => handleFilterGeolocationType('estado')}
                  checked={filters.geoType === 'estado'}
                />
                <label className="form-check-label" htmlFor="bulletinGeoEstado">
                  Estado
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="bulletinGeoType"
                  id="bulletinGeoRegiao"
                  onChange={() => handleFilterGeolocationType('região')}
                  checked={filters.geoType === 'região'}
                />
                <label className="form-check-label" htmlFor="bulletinGeoRegiao">
                  Região
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="bulletinGeoType"
                  id="bulletinGeoRaio"
                  onChange={() => handleFilterGeolocationType('localização')}
                  checked={filters.geoType === 'localização'}
                />
                <label className="form-check-label" htmlFor="bulletinGeoRaio">
                  Raio de atuação
                </label>
              </div>
            </div>
          </div>
        </div>

        {filters.geoType === 'estado' && (
          <>
            <div className="row">
              <div className="col-md-12 d-flex justify-content-between align-items-center">
                <label htmlFor="stateSearch" className="m-0">
                  Estado
                </label>
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0"
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      states: States.map((s) => s.value),
                      cities: [],
                    }))
                  }
                >
                  Todos os Estados
                </button>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12">
                <Select
                  isMulti
                  value={States.filter((s) => filters.states.includes(s.value))}
                  closeMenuOnSelect={false}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
                    setFilters((prev) => ({
                      ...prev,
                      states: values,
                      cities: [],
                    }));
                  }}
                  options={States}
                  placeholder="Selecione os Estados"
                />
              </div>
            </div>
            <div className="row my-3">
              <div className="col-md-12">
                <label htmlFor="citySearch" className="m-0">
                  Cidade
                </label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  value={citiesList.filter((c) => filters.cities.includes(Number(c.value)))}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions
                      ? selectedOptions.map((opt) => Number(opt.value))
                      : [];
                    setFilters((prev) => ({
                      ...prev,
                      cities: values,
                    }));
                  }}
                  options={citiesList}
                  noOptionsMessage={() => 'Selecione um estado'}
                  placeholder="Selecione as Cidades"
                />
              </div>
            </div>
          </>
        )}

        {filters.geoType === 'região' && (
          <div className="row mb-3">
            <div className="col-md-12">
              <label htmlFor="RegionSearch" className="m-0">
                Região
              </label>
              <Select
                name="RegionSearch"
                value={filters.region}
                onChange={(selected) => {
                  const region = selected as { label: string; value: string };
                  const regionStates = RegionsStates[region.value] || [];
                  setFilters((prev) => ({
                    ...prev,
                    region,
                    states: regionStates.map((s) => s.value),
                    cities: [],
                  }));
                }}
                options={Regions}
                placeholder="Selecione uma Região"
              />
            </div>
          </div>
        )}

        {filters.geoType === 'localização' && (
          <div className="row mb-3">
            <div className="col-md-12">
              {filters.address !== '' && (
                <div className="alert alert-secondary">{`${filters.radius / 1000} km de ${
                  filters.address
                }`}</div>
              )}
              <button
                type="button"
                className="btn btn-link m-0 p-0 btn-block mt-2"
                onClick={() => setModalLocationOpened(true)}
              >
                {filters.address === '' ? 'Buscar Endereço' : 'Editar Endereço'}
              </button>
            </div>
          </div>
        )}
        <div className="row my-3">
          <div className="col-md-12">
            <label className="m-0">Órgão</label>
            <input
              type="text"
              name="orgao"
              className="form-control"
              placeholder="Buscar por órgão"
              value={filters.orgao}
              onChange={handleFilterChange}
            />
          </div>
        </div>
        {selected === 'licitacoes' && (
          <>
            <div className="row my-3">
              <div className="col-md-12">
                <label className="m-0">Data de validade</label>
                <div className="date-range-full">
                  <DateRangePicker
                    startDate={filters.validityStart ? moment(filters.validityStart) : null}
                    startDateId="validityStart_date_id"
                    minimumNights={0}
                    endDate={filters.validityEnd ? moment(filters.validityEnd) : null}
                    endDateId="validityEnd_date_id"
                    onDatesChange={(dates) =>
                      setFilters((prev) => ({
                        ...prev,
                        validityStart: dates.startDate ? dates.startDate.format('YYYY-MM-DD') : '',
                        validityEnd: dates.endDate ? dates.endDate.format('YYYY-MM-DD') : '',
                      }))
                    }
                    focusedInput={focusedValidityDate}
                    onFocusChange={(focusedDate) => setFocusedValidityDate(focusedDate)}
                    startDatePlaceholderText="Dê"
                    endDatePlaceholderText="Até"
                    hideKeyboardShortcutsPanel
                    displayFormat="DD/MM/YYYY"
                    isOutsideRange={() => false}
                    showClearDates
                    small
                    numberOfMonths={1}
                  />
                </div>
              </div>
            </div>
            <div className="row my-3">
              <div className="col-md-12">
                <div className="mb-1">Situação</div>
                <Select
                  isMulti
                  value={situationOptions.filter((s) => filters.situations.includes(s.value))}
                  closeMenuOnSelect={false}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];

                    setFilters((prev) => ({
                      ...prev,
                      situations: values,
                    }));
                  }}
                  options={situationOptions}
                  placeholder="Filtre por situação"
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-12">
                <label className="m-0">Status</label>
                <Select
                  isMulti
                  value={statusOptions.filter((s) => filters.status?.includes(s.value))}
                  closeMenuOnSelect={false}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];

                    setFilters((prev) => ({
                      ...prev,
                      status: values,
                    }));
                  }}
                  options={statusOptions}
                  placeholder="Filtre por status"
                />
              </div>
            </div>
          </>
        )}

        {selected === 'acompanhamentos' && (
          <>
            <div className="row my-3">
              <div className="col-md-12">
                <label className="m-0">Data de publicação</label>
                <div className="date-range-full">
                  <DateRangePicker
                    startDate={filters.publicationStart ? moment(filters.publicationStart) : null}
                    startDateId="publicationStart_date_id"
                    minimumNights={0}
                    endDate={filters.publicationEnd ? moment(filters.publicationEnd) : null}
                    endDateId="publicationEnd_date_id"
                    onDatesChange={(dates) =>
                      setFilters((prev) => ({
                        ...prev,
                        publicationStart: dates.startDate
                          ? dates.startDate.format('YYYY-MM-DD')
                          : '',
                        publicationEnd: dates.endDate ? dates.endDate.format('YYYY-MM-DD') : '',
                      }))
                    }
                    focusedInput={focusedPublicationDate}
                    onFocusChange={(focusedDate) => setFocusedPublicationDate(focusedDate)}
                    startDatePlaceholderText="Dê"
                    endDatePlaceholderText="Até"
                    hideKeyboardShortcutsPanel
                    displayFormat="DD/MM/YYYY"
                    isOutsideRange={() => false}
                    showClearDates
                    small
                    numberOfMonths={1}
                  />
                </div>
              </div>
            </div>
            <div className="row my-3">
              <div className="col-md-12">
                <div className="mb-1">Esfera</div>
                <Select
                  isMulti
                  value={esferasOptions.filter((e) => filters.esfera.includes(e.value))}
                  closeMenuOnSelect={false}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
                    setFilters((prev) => ({
                      ...prev,
                      esfera: values,
                    }));
                  }}
                  options={esferasOptions}
                  placeholder="Filtre por esfera"
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-12">
                <label className="m-0">Status</label>
                <Select
                  isMulti
                  value={statusOptions.filter((s) => filters.status?.includes(s.value))}
                  closeMenuOnSelect={false}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
                    setFilters((prev) => ({
                      ...prev,
                      status: values,
                    }));
                  }}
                  options={statusOptions}
                  placeholder="Filtre por status"
                />
              </div>
            </div>
          </>
        )}
      </form>
      <div className="filter-actions d-print-none">
        {onlineSearch.filterActive.id <= 0 && !updateFilter && (
          <>
            <div className="save-row">
              <span className="m-0">Salvar filtro?</span>
              <div className="custom-control custom-switch">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="saveFilterSwitch"
                  checked={saveFilter}
                  onChange={() => setSaveFilter((prev) => !prev)}
                />
                <label className="custom-control-label" htmlFor="saveFilterSwitch" />
              </div>
            </div>
            {saveFilter && (
              <input
                type="text"
                className="form-control mb-2"
                placeholder="Digite o nome do filtro..."
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
              />
            )}
            <button type="button" className="btn btn-primary w-100 mb-2" onClick={handleSearch}>
              {saveFilter ? 'Salvar e Filtrar' : 'Filtrar'}
            </button>
            <button type="button" className="btn btn-link w-100 p-0" onClick={handleClearFilters}>
              Limpar filtro
            </button>
          </>
        )}
        {updateFilter && (
          <>
            <input
              type="text"
              className="form-control mb-2"
              placeholder="Digite o nome do filtro..."
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
            <button
              type="button"
              className={`btn w-100 mb-2 ${
                filterName.trim().length > 0 ? 'btn-primary' : 'btn-secondary'
              }`}
              disabled={filterName.trim().length === 0}
              onClick={handleUpdateFilter}
            >
              Atualizar e Filtrar
            </button>
          </>
        )}
        {onlineSearch.filterActive.id > 0 && !updateFilter && (
          <div className="text-center">
            <button type="button" className="btn btn-primary w-100 mb-2" onClick={handleNewSearch}>
              Nova pesquisa
            </button>
          </div>
        )}
      </div>
      <ModalRemoveSearch
        modalRemoveSearchOpened={modalRemoveSearchOpened}
        setModalRemoveSearchOpened={setModalRemoveSearchOpened}
        handleDeleteFilter={handleDeleteFilter}
      />
      <BulletinIaChat
        show={showIaChat}
        onHide={() => {
          setShowIaChat(false);
          setEditingCustomSearchId(null);
          setAutoSendSave(false);
        }}
        bulletinFilterId={Number(bulletinId)}
        editingFilterId={editingCustomSearchId}
        autoSendSave={autoSendSave}
        onAutoSendConsumed={() => setAutoSendSave(false)}
        onResetEditing={() => setEditingCustomSearchId(null)}
        onApplyFilters={handleApplyIaFilters}
        onSaveFilter={handleSaveIaFilter}
      />
      <ModalLocation
        modalLocationOpened={modalLocationOpened}
        setModalLocationOpened={setModalLocationOpened}
        setAddressSelected={setAddressSelected}
        addressSelected={addressSelected}
        bounds={bounds}
        setBounds={setBounds}
        position={position}
        setPosition={setPosition}
        radius={filters.radius}
        setRadius={handleRadiusChange}
        setCitiesGeolocation={setCitiesGeolocation}
      />
    </Container>
  );
};

export default BulletinSearch;
